import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { useResetPassword } from "@/api/client/auth"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { isIpNotAllowedError } from "@/features/ip-restriction"

const passwordResetSchema = z
  .object({
    newPassword: z
      .string({ message: "パスワードを入力してください" })
      .min(8, "8文字以上で入力してください")
      .regex(/^[0-9A-Za-z]+$/, "半角英数字のみで入力してください"),
    confirmPassword: z.string().min(1, "確認用パスワードを入力してください"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "パスワードが一致しません",
    path: ["confirmPassword"],
  })

type PasswordResetFormData = z.infer<typeof passwordResetSchema>

export function PasswordResetPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")

  useEffect(() => {
    if (!token) {
      toast.error("無効なリンクです")
      navigate("/login")
    }
  }, [token, navigate])

  const form = useForm<PasswordResetFormData>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  })

  const { mutateAsync: resetPassword, isPending } = useResetPassword({
    onSuccess: () => {
      toast.success("パスワードを再設定しました")
      navigate("/login")
    },
    onError: (error) => {
      // IP アドレス制限による拒否は、API クライアントが理由を出す。
      if (isIpNotAllowedError(error)) {
        return
      }
      toast.error("パスワードの再設定に失敗しました")
    },
  })

  const onSubmit = async (data: PasswordResetFormData) => {
    if (!token) {
      toast.error("無効なリンクです")
      return
    }
    try {
      await resetPassword({
        newPassword: data.newPassword,
        token: token,
      })
    } catch (error) {
      console.error("Reset password error:", error)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-1 flex flex-col items-center px-4 pb-8">
        <h2 className="text-lg font-bold text-slate-700 mb-10">新しいパスワードを設定</h2>

        <div className="w-full max-w-sm space-y-10">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-600">
                      新しいパスワード
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="*****"
                        type="password"
                        autoComplete="new-password"
                        className="h-11"
                        {...field}
                      />
                    </FormControl>
                    <p className="text-sm text-slate-400">
                      半角英数字、8文字以上で入力してください
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-600">
                      新しいパスワード（確認用）
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="*****"
                        type="password"
                        autoComplete="new-password"
                        className="h-11"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4 pt-3">
                <Button
                  type="submit"
                  size="lg"
                  className="w-full h-11 font-bold"
                  disabled={form.formState.isSubmitting}
                  loading={isPending}
                >
                  パスワードを再設定する
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="w-full h-11 font-bold text-primary"
                  disabled={form.formState.isSubmitting || isPending}
                  asChild
                >
                  <Link to="/login">ログイン画面に戻る</Link>
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}
